import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getParcel, createParcel, updateParcel } from '../../api/parcels';
import { useParcelStore } from '../../store/parcelStore';
import Button from '../../components/common/Button';
import Input, { Select } from '../../components/common/Input';
import Card from '../../components/common/Card';
import { FullPageSpinner } from '../../components/common/Spinner';

const CROP_TYPES = [
  { value: 'wheat', label: 'Пченица' },
  { value: 'corn', label: 'Пченка' },
  { value: 'soybean', label: 'Соја' },
  { value: 'rice', label: 'Ориз' },
  { value: 'barley', label: 'Јачмен' },
  { value: 'sunflower', label: 'Сончоглед' },
  { value: 'rapeseed', label: 'Репка' },
  { value: 'sugar_beet', label: 'Шеќерна репка' },
  { value: 'potato', label: 'Компир' },
  { value: 'tomato', label: 'Домат' },
  { value: 'other', label: 'Друго' },
];

const initialForm = {
  parcel_name: '',
  latitude: '',
  longitude: '',
  size_m2: '',
  crop_type: '',
};

export default function ParcelForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addParcel, updateParcel: storeUpdate } = useParcelStore();
  const isEditing = Boolean(id);

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEditing);

  useEffect(() => {
    if (isEditing) {
      getParcel(id)
        .then((res) => {
          const p = res.data;
          setForm({
            parcel_name: p.parcel_name || '',
            latitude: p.latitude || '',
            longitude: p.longitude || '',
            size_m2: p.size_m2 || '',
            crop_type: p.crop_type || '',
          });
        })
        .catch(() => {
          toast.error('Неуспешно вчитување на парцелата');
          navigate('/parcels');
        })
        .finally(() => setFetchLoading(false));
    }
  }, [id, isEditing, navigate]);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setErrors((er) => ({ ...er, [e.target.name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isEditing) {
        const res = await updateParcel(id, form);
        storeUpdate(Number(id), res.data);
        toast.success('Парцелата е ажурирана');
      } else {
        const res = await createParcel(form);
        addParcel(res.data);
        toast.success('Парцелата е создадена');
      }
      navigate('/parcels');
    } catch (err) {
      const data = err.response?.data || {};
      if (typeof data === 'object') {
        const fieldErrors = {};
        Object.keys(data).forEach((k) => {
          fieldErrors[k] = Array.isArray(data[k]) ? data[k][0] : data[k];
        });
        setErrors(fieldErrors);
        toast.error('Ве молиме исправете ги грешките подолу');
      } else {
        toast.error('Неуспешно зачувување на парцелата');
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) return <FullPageSpinner />;

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">
          {isEditing ? 'Уреди парцела' : 'Додај парцела'}
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {isEditing ? 'Ажурирај ги информациите за парцелата' : 'Регистрирај ново поле или парцела'}
        </p>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Име на парцелата"
            name="parcel_name"
            value={form.parcel_name}
            onChange={handleChange}
            error={errors.parcel_name}
            required
            placeholder="Северно поле"
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Географска ширина"
              name="latitude"
              type="number"
              step="any"
              value={form.latitude}
              onChange={handleChange}
              error={errors.latitude}
              required
              placeholder="41.9981"
            />
            <Input
              label="Географска должина"
              name="longitude"
              type="number"
              step="any"
              value={form.longitude}
              onChange={handleChange}
              error={errors.longitude}
              required
              placeholder="21.4254"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Површина (м²)"
              name="size_m2"
              type="number"
              step="0.01"
              min="0"
              value={form.size_m2}
              onChange={handleChange}
              error={errors.size_m2}
              required
              placeholder="50000"
            />
            <Select
              label="Вид на култура"
              name="crop_type"
              value={form.crop_type}
              onChange={handleChange}
              error={errors.crop_type}
            >
              <option value="">Изберете култура...</option>
              {CROP_TYPES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </Select>
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="submit" loading={loading}>
              {isEditing ? 'Ажурирај парцела' : 'Создај парцела'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => navigate('/parcels')}>
              Откажи
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
